import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, TrendingUp } from "lucide-react";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { VersionNavigation } from "@/components/VersionNavigation";

interface UserContribution {
  user_id: number;
  username: string;
  submitted_count: number;
  accepted_count: number;
}

interface TranslationFileStats {
  id: number;
  name: string;
  total_texts: number;
  accepted_count: number;
  translation_percentage: number;
}

interface StatisticsData {
  translation_file?: {
    id: number;
    name: string;
  };
  version?: {
    id: number;
    name: string;
  };
  statistics: {
    total_texts: number;
    accepted_count: number;
    submitted_count: number;
    translation_percentage: number;
    waiting_for_translation: number;
    last_submission_at: string | null;
    last_acceptance_at: string | null;
  };
  translation_files?: TranslationFileStats[];
  user_contributions: UserContribution[];
}

const formatDate = (dateString: string | null) => {
  if (!dateString) return "Nincs adat";
  const date = new Date(dateString);
  return date.toLocaleString("hu-HU", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Statistics = () => {
  const { gameId, versionId, fileId } = useParams();

  // Build the correct API endpoint based on the route
  let endpoint = "";

  if (fileId) {
    // Try both possible routes for file statistics
    endpoint = `/api/games/${gameId}/versions/${versionId}/translation_files/${fileId}/statistics`;
  } else {
    // Version-level statistics
    endpoint = `/api/games/${gameId}/versions/${versionId}/statistics`;
  }

  const { data, isLoading, error } = useQuery<StatisticsData>({
    queryKey: ["statistics", fileId || versionId],
    queryFn: async () => {
      const response = await fetch(endpoint);
      if (!response.ok) throw new Error("Statisztika betöltése sikertelen");
      return response.json();
    },
  });

  const { data: gameDetails } = useQuery({
    queryKey: ["gameDetails", gameId],
    queryFn: async () => {
      const response = await fetch(`/api/games/${gameId}`);
      if (!response.ok) throw new Error("Játék adatok betöltése sikertelen");
      return response.json();
    },
    enabled: !!gameId,
  });

  const { data: versionDetails } = useQuery({
    queryKey: ["versionDetails", gameId, versionId],
    queryFn: async () => {
      const response = await fetch(`/api/games/${gameId}/versions/${versionId}`);
      if (!response.ok) throw new Error("Verzió adatok betöltése sikertelen");
      return response.json();
    },
    enabled: !!gameId && !!versionId,
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center">Betöltés...</div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Hiba a statisztika betöltésekor: {error?.message || "Ismeretlen hiba"}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const stats = data.statistics;
  const progressPercentage = stats.translation_percentage;

  return (
    <div className="p-6 space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games`}>Játékok</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}`}>{gameDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbLink asChild><Link to={`/games/${gameId}/versions/${versionId}`}>Verzió: {versionDetails?.name}</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>Statisztika</BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <VersionNavigation gameId={gameId} versionId={versionId} />

      {/* Title */}
      <div>
        <h1 className="text-3xl font-bold">
          {data.translation_file?.name || data.version?.name} - Statisztika
        </h1>
        <p className="text-gray-600 mt-1">
          Fordítási statisztika és közreműködők összesítése
        </p>
      </div>

      {/* Main Statistics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {/* Translation Percentage */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Fordítási Progszázalék</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{progressPercentage}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.accepted_count} / {stats.total_texts} szöveg fordítva
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div
                className="bg-green-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </CardContent>
        </Card>

        {/* Accepted Count */}
        <Card className="bg-slate-950 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Elfogadott</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{stats.accepted_count}</div>
            <p className="text-xs text-muted-foreground mt-1">Fordítás</p>
          </CardContent>
        </Card>

        {/* Submitted Count */}
        <Card className="bg-slate-950 border-slate-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Beküldött</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{stats.submitted_count}</div>
            <p className="text-xs text-muted-foreground mt-1">Függőben</p>
          </CardContent>
        </Card>

        {/* Waiting */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Várakozás alatt</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{stats.waiting_for_translation}</div>
            <p className="text-xs text-muted-foreground mt-1">Fordítandó</p>
          </CardContent>
        </Card>
      </div>

      {/* Last Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Utolsó Aktivitás</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <p className="text-sm font-medium text-gray-600">Utolsó Beküldés</p>
              <p className="text-lg font-semibold mt-1">{formatDate(stats.last_submission_at)}</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-2">
              <p className="text-sm font-medium text-gray-600">Utolsó Elfogadás</p>
              <p className="text-lg font-semibold mt-1">{formatDate(stats.last_acceptance_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Translation Files Statistics (if version level) */}
      {data.translation_files && data.translation_files.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Fájlonkénti Statisztika</CardTitle>
            <CardDescription>Az összes fordítási fájl fordítási progressze</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fájl Neve</TableHead>
                    <TableHead className="text-right">Összes Szöveg</TableHead>
                    <TableHead className="text-right">Elfogadott</TableHead>
                    <TableHead className="text-right">Fordítási %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.translation_files.map((file) => (
                    <TableRow key={file.id}>
                      <TableCell className="font-medium">
                        <Link
                          to={`/game/${gameId}/version/${versionId}/translate/${file.id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {file.name}
                        </Link>
                      </TableCell>
                      <TableCell className="text-right">{file.total_texts}</TableCell>
                      <TableCell className="text-right text-green-600 font-semibold">
                        {file.accepted_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-24 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${file.translation_percentage}%` }}
                            />
                          </div>
                          <span className="font-semibold">{file.translation_percentage}%</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* User Contributions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Közreműködők
          </CardTitle>
          <CardDescription>
            Ki mennyit küldött be és mennyi van abból elfogadva
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Felhasználó</TableHead>
                  <TableHead className="text-right">Beküldött</TableHead>
                  <TableHead className="text-right">Elfogadott</TableHead>
                  <TableHead className="text-right">Elfogadás %</TableHead>
                  <TableHead className="text-right">Státusz</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.user_contributions.length > 0 ? (
                  data.user_contributions
                    .sort((a, b) => b.submitted_count - a.submitted_count)
                    .map((user) => {
                      const acceptancePercentage =
                        user.submitted_count > 0
                          ? ((user.accepted_count / user.submitted_count) * 100).toFixed(1)
                          : "0";
                      return (
                        <TableRow key={user.user_id}>
                          <TableCell className="font-medium">{user.username}</TableCell>
                          <TableCell className="text-right">
                            <span className="bg-blue-900 text-blue-200 px-2 py-1 rounded">
                              {user.submitted_count}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="bg-green-900 text-green-200 px-2 py-1 rounded">
                              {user.accepted_count}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 bg-gray-200 rounded-full h-2">
                                <div
                                  className="bg-green-500 h-2 rounded-full"
                                  style={{ width: `${acceptancePercentage}%` }}
                                />
                              </div>
                              <span className="font-semibold w-12 text-right">
                                {acceptancePercentage}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            {user.accepted_count === user.submitted_count && user.submitted_count > 0 ? (
                              <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">
                                Tökéletes
                              </span>
                            ) : user.accepted_count > 0 ? (
                              <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                                Jó
                              </span>
                            ) : (
                              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs">
                                Függőben
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-4">
                      Még nincs közreműködő
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Statistics;